import { useEffect, useState } from 'react';
import { Tag, Image, Tabs, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { TemplateDetails } from 'PhishingManager/pages/CampaignSteps';
import CommonModal from 'PhishingManager/components/Modal';
import { DIFFICULTY_COLOR } from 'PhishingManager/PhishingManager.constants';
import { capitalize } from 'PhishingManager/utils';
import { Icon } from 'Components';

interface PhishingModalProps {
    currentStep: number;
    loading: boolean;
    visible: boolean;
    onClose: () => void;
    onNext: () => void;
    onLanguageChange: (language: number) => void;
    templateDetails: TemplateDetails | null;
    showConfirmBtn: boolean;
}

const PhishingModal = ({
    currentStep = 1,
    loading,
    visible,
    onClose,
    onNext,
    onLanguageChange,
    templateDetails,
    showConfirmBtn
}: PhishingModalProps) => {
    const [activeTab, setActiveTab] = useState('email');
    const { TabPane } = Tabs;
    const { t } = useTranslation();

    const diff =
        DIFFICULTY_COLOR[capitalize(templateDetails?.difficulty || '')] ||
        DIFFICULTY_COLOR.Beginner;

    const handleTemplateChange = (lang: {
        id: number;
        name?: string | undefined;
    }) => {
        onLanguageChange(lang.id);
    };

    const openInNewTab = (htmlContent: string) => {
        if (!htmlContent) return;

        const newWindow = window.open();
        newWindow?.document.write(htmlContent);
        newWindow?.document.close();
    };

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (event.data === 'IFRAME_EMAIL_CLICKED') {
                openInNewTab(templateDetails?.email_template_html ?? '');
            }

            if (event.data === 'IFRAME_LANDING_CLICKED') {
                openInNewTab(templateDetails?.landing_page_html ?? '');
            }
        };

        window.addEventListener('message', handleMessage);

        return () => window.removeEventListener('message', handleMessage);
    }, [templateDetails]);

    return (
        <CommonModal
            visible={visible}
            showConfirmBtn={showConfirmBtn}
            confirmBtnDisabled={loading}
            confirmText={t('phishingManager.phishingModal.confirmText')}
            cancelText={t('phishingManager.phishingModal.cancelText')}
            iconType={null}
            onClose={onClose}
            onConfirm={onNext}
            modalTitle={
                templateDetails?.name ||
                t('phishingManager.phishingModal.defaultTemplateName')
            }
            parentClass="phishing-preview-modal"
        >
            <Spin spinning={loading}>
                <div className="description-section preview-header-card">
                    <div className="branding-logo-wrapper">
                        <Icon type="LogoModal" />
                    </div>

                    <div className="section-image">
                        <Image
                            src={
                                templateDetails?.image_path ||
                                './placeholderImage.png'
                            }
                            alt={t('phishingManager.phishingModal.imageAlt')}
                            preview={false}
                        />

                        <Tag
                            style={{
                                background: diff.bg,
                                color: diff.color,
                                border: 'none'
                            }}
                            className="badge-beginner"
                        >
                            {templateDetails?.difficulty}
                        </Tag>
                    </div>

                    <div className="section-content">
                        <h3 className="description-title">
                            {t('phishingManager.phishingModal.description.title')}
                        </h3>

                        <p className="description-text">
                            {templateDetails?.description ||
                                t('phishingManager.phishingModal.description.empty')}
                        </p>

                        <p className="description-category">
                            <strong>
                                {t('phishingManager.phishingModal.description.category')}{' '}
                            </strong>
                            {templateDetails?.categories
                                ?.map((c) => c.name)
                                .join(', ') || '—'}
                        </p>
                    </div>
                </div>

                <div className="tabs-section">
                    <Tabs activeKey={activeTab} onChange={setActiveTab}>
                        <TabPane
                            tab={t('phishingManager.phishingModal.tabs.email')}
                            key="email"
                        >
                            <TemplatePreview
                                title={t(
                                    'phishingManager.phishingModal.tabs.emailPreview'
                                )}
                                html={templateDetails?.email_template_html}
                                postMessageKey="IFRAME_EMAIL_CLICKED"
                            />
                        </TabPane>

                        <TabPane
                            tab={t('phishingManager.phishingModal.tabs.landingPage')}
                            key="landing_page"
                        >
                            <TemplatePreview
                                title={t(
                                    'phishingManager.phishingModal.tabs.landingPagePreview'
                                )}
                                html={templateDetails?.landing_page_html}
                                postMessageKey="IFRAME_LANDING_CLICKED"
                            />
                        </TabPane>
                    </Tabs>
                </div>

                <div className="language-section">
                    <label className="language-label">
                        {currentStep !== 4
                            ? t('phishingManager.phishingModal.language.select')
                            : t('phishingManager.phishingModal.language.selected')}

                        {currentStep !== 4 ? (
                            <span className="required">*</span>
                        ) : null}
                    </label>

                    <div className="language-tags">
                        {showConfirmBtn ? (
                            templateDetails?.languages?.map(
                                (lang: {
                                    id: number;
                                    name?: string | undefined;
                                }) => (
                                    <Tag
                                        key={lang.id}
                                        onClick={() =>
                                            handleTemplateChange(lang)
                                        }
                                        className={`language-tag ${
                                            templateDetails?.selected_packaged_template_language_id ===
                                            lang.id
                                                ? 'selected'
                                                : ''
                                        }`}
                                    >
                                        {lang.name}
                                    </Tag>
                                )
                            )
                        ) : (
                            <Tag className="language-tag selected">
                                {(templateDetails?.languages || []).find(
                                    (item) =>
                                        item.id ===
                                        templateDetails?.selected_packaged_template_language_id
                                )?.name ||
                                    t('phishingManager.phishingModal.language.default')}
                            </Tag>
                        )}
                    </div>
                </div>
            </Spin>
        </CommonModal>
    );
};

export default PhishingModal;

const TemplatePreview = ({
    title,
    html,
    postMessageKey
}: {
    title: string;
    html?: string;
    postMessageKey: string;
}) => {
    const injectIframeLogic = (html: string | undefined): string => {
        if (!html) return '';

        const logicScript = `
            <style>
            a { pointer-events: none !important; cursor: default !important; }
            html, body { cursor: pointer; margin: 0; padding: 0; }
            </style>
            <script>
            document.addEventListener('click', () => {
                window.parent.postMessage('${postMessageKey}', '*');
            });
            </script>
        `;

        if (html.includes('</head>')) {
            return html.replace('</head>', `${logicScript}</head>`);
        }

        if (html.includes('<body>')) {
            return html.replace(
                '<body>',
                `<head>${logicScript}</head><body>`
            );
        }

        return `<head>${logicScript}</head>${html}`;
    };

    return (
        <div className="tab-content">
            <div>
                <iframe
                    title={title}
                    srcDoc={injectIframeLogic(html) || ''}
                />
            </div>
        </div>
    );
};